import { useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { usePieceDispatch } from '../../model/piece-context';
import type { AudioLayer } from '../../model/types';

const readAudioDuration = (url: string): Promise<number> =>
  new Promise<number>((resolve, reject) => {
    const el = document.createElement('audio');
    el.preload = 'metadata';
    el.src = url;
    el.addEventListener('loadedmetadata', () => {
      if (isFinite(el.duration) && el.duration > 0) resolve(el.duration);
      else reject(new Error('Audio metadata missing duration'));
    });
    el.addEventListener('error', () => reject(new Error('Audio failed to load')));
  });

/** Uploads an audio file as a new audio layer in the main model. */
export const useAudioUpload = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const dispatch = usePieceDispatch();

  const open = useCallback(() => inputRef.current?.click(), []);

  const handleChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      event.target.value = '';

      const url = URL.createObjectURL(file);
      let durationSec: number;
      try {
        durationSec = await readAudioDuration(url);
      } catch {
        URL.revokeObjectURL(url);
        return;
      }

      const layer: AudioLayer = {
        id: uuidv4(),
        kind: 'audio',
        visible: true,
        locked: false,
        label: file.name,
        start: 0,
        duration: Math.round(durationSec * 1000),
        fit: 'fixed',
        content: { src: url }
      };
      dispatch({ type: 'addAudioLayer', layer });
    },
    [dispatch]
  );

  return { open, inputRef, handleChange };
};
